// HOW ONE LEAD'S OUTREACH DRAFT GOT HERE — the provenance graph behind the
// arrow on a lead card, drawn by DraftFlowView's Developer view.
//
// WHAT THIS FILE IS FOR, and the only thing it is for: proving that the email
// on screen was not made up. Not explaining the pipeline, not teaching the
// cadence, not reassuring anyone. A reader opens this to audit one email, and
// every node has to hand them something they can check.
//
// THE RULE THAT DECIDES WHAT GOES IN A NODE:
//
//     DATA, NOT METHOD.
//
// "Touch number = deduped outbound touches + 1: 1 prior touch puts this lead at
// touch 2" is method — it describes the arithmetic. "Follows the email 'Masonry
// takeoffs at Apex' sent Jul 28; 1 prior outbound touch on record" is data: the
// reader can go and look at that email. Every line below is a value this
// receipt actually carries, and a value it does not carry is a line NOT
// WRITTEN — never a vaguer sentence, never a description of what the step would
// have done.
//
// THE STRONGEST EVIDENCE IS THE RUN'S OWN WORDS. While the run executed, every
// node wrote one sentence per lead about what it found (agentRun.js#makeTracer)
// — "3 open CRM tasks on HubSpot company 12345 — a colleague is already on it",
// "no inbound after our last touch in the conversations read". Those sentences
// were written at decision time, are stored verbatim on the receipt, and are
// never re-derived. Each node here carries the one belonging to it as
// `recorded`, and the panel renders it first. Nothing in this file paraphrases
// them, back-fills a missing one, or re-runs a check to fill a gap: an absent
// record is shown as absent.
//
// WHAT IS DELIBERATELY NOT HERE — because the app does not hold it, and a node
// citing evidence the receipt lacks would be the exact hallucination this view
// exists to catch: HubSpot note bodies, task titles (the run counts open tasks
// and never reads their subjects), deal names/amounts/stages/owners, per-touch
// dates other than the last, a reply's text on a drafted lead (a lead that
// really replied is pulled out before a draft exists), and full email bodies.
import {
  readFacts,
  readResearch,
  formatChicagoDay,
  followupPersonalizes,
  RESEARCH_LABELS,
} from './outreachDraft.js'
import { researchLinks } from './draftNarrative.js'
import { touchLabel } from './cadence.js'

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`
const clean = (v) => String(v ?? '').trim()

// The research signal for one card, read off whichever holder carries it: the
// intel snapshot (the engine's merged row) when it does, else the receipt stub
// (whose bounded copies exist exactly for old-receipt-safe retelling). An
// unresearched lead — every old receipt — comes back { researched: false }.
function researchOf(lead = {}, facts = null) {
  const fromFacts = readResearch(typeof facts === 'object' && facts ? facts : {})
  return fromFacts.researched ? fromFacts : readResearch(lead)
}

// The researched source entry the draft ACTUALLY used, found in the draft's own
// sources[] — the record of the facts it wrote from. null when the draft wrote
// no researched claim (unresearched, or research degraded to row facts).
function researchedEntryOf(draft = {}) {
  return (
    (Array.isArray(draft.sources) ? draft.sources : []).find(
      (s) => s && s.label && s.value && RESEARCH_LABELS.includes(s.label),
    ) || null
  )
}

// ── the run's own record ────────────────────────────────────────────────────
//
// `prov` is what the receipt stored about THIS lead: the per-node trace the run
// wrote while it was deciding, plus the node titles from the run's own graph so
// a record can name the step that wrote it.
//
//   { trace: Map<nodeId, {verdict, note}>, nodeTitles: Map<nodeId, string>,
//     traceState: 'present' | 'shed' | 'dropped' | 'none' }
//
// Every field is optional. A receipt whose trace was shed under storage
// pressure, a call row (which never gets a trace), and every receipt from
// before this existed all land on the same path: no `recorded` block, and the
// node falls back to the evidence its stub carries.
function quote(prov, nodeId) {
  const entry = prov?.trace?.get?.(nodeId)
  if (!entry || !clean(entry.note)) return null
  return {
    nodeId,
    nodeTitle: prov?.nodeTitles?.get?.(nodeId) || nodeId,
    verdict: clean(entry.verdict),
    note: clean(entry.note),
  }
}

// Attach a record to a node, when one exists. Written as a helper so no node
// can accidentally invent the shape, and so `recorded: undefined` never ships
// as a key.
const withRecord = (node, recorded) => (recorded ? { ...node, recorded } : node)

// ── THE CODE THAT RAN ───────────────────────────────────────────────────────
//
// Every node carries `exec`: an ARRAY of execution records, each naming a
// piece of code that actually executed for this step — the run node's own
// descriptor (agentRun.js NODE_EXEC, stored on the receipt with the pass), and,
// where a lambda did the work, THAT lambda's own record returned at run time
// (the enrich-worker's on the dossier, the draft route's on the draft). The
// Developer view renders them as file → function → lambda → model → prompt
// sha, so a reader who disagrees with a step is handed the exact code to go
// and correct. An absent record is an absent record: the view says "no
// execution record on this receipt", never a guess at what probably ran.
//
// `prov.nodeExec` is a Map<runNodeId, exec> lifted off msg.passes[].exec by
// Chat.jsx#provenanceOf. Empty on every receipt written before the descriptors
// existed.
function runExec(prov, ...nodeIds) {
  const out = []
  for (const id of nodeIds) {
    const rec = prov?.nodeExec?.get?.(id)
    if (rec && typeof rec === 'object') {
      out.push({ ...rec, nodeId: id, nodeTitle: prov?.nodeTitles?.get?.(id) || id })
    }
  }
  return out
}
const withExec = (node, exec) => (Array.isArray(exec) && exec.length ? { ...node, exec } : node)

// ── the directory row, as fields ────────────────────────────────────────────
//
// The row is the single largest source of everything an email says, so the node
// that shows it prints the COLUMN NAME next to the VALUE. A reader comparing
// the email against this list is doing exactly the audit this view exists for,
// and column names are what they would search the directory by.
//
// Snapshot-only fields (score, owners, estimators…) appear only for a lead
// somebody on this account has actually opened — intel.js writes a snapshot on
// the lead detail page and nowhere else. Absent, the node says so rather than
// leaving the reader to wonder whether the row was thin or merely unread.
const ROW_COLUMNS = [
  ['city_state', ['city_state', 'cityState', 'location']],
  ['trade', ['trade', 'trades']],
  ['contact_name', ['contact_name', 'contactName', 'estimator']],
  ['contact_role', ['contact_role', 'contactRole']],
  ['contact_phone', ['contact_phone', 'contactPhone', 'phone']],
  ['service_area', ['service_area', 'serviceArea']],
  ['commercial_residential', ['commercial_residential', 'commercialResidential']],
  ['annual_volume', ['annual_volume', 'annualVolume']],
  ['company_size', ['company_size', 'companySize']],
  ['established', ['established', 'founded']],
  ['owners', ['owners']],
  ['estimators', ['estimators', 'has_estimator', 'hasEstimator']],
  ['score', ['score', 'lead_score', 'leadScore']],
  ['score_reasoning', ['score_reasoning', 'scoreReasoning']],
  ['sales_angles', ['sales_angles', 'salesAngles']],
  ['objections', ['objections']],
  ['website', ['website', 'domain']],
  ['enriched_source', ['enriched_source', 'enrichedSource']],
]

// How many column lines one node prints before it stops and counts the rest. A
// snapshot can carry every column the directory has; a wall of thirty is not
// more auditable than a dozen, it is just a wall.
const ROW_COLUMN_LIMIT = 12

function rowColumnLines(facts, lead) {
  const src = typeof facts === 'object' && facts ? facts : {}
  const lines = []
  let more = 0
  for (const [column, keys] of ROW_COLUMNS) {
    const key = keys.find((k) => clean(src[k]))
    if (!key) continue
    if (lines.length >= ROW_COLUMN_LIMIT) {
      more += 1
      continue
    }
    lines.push(`${column} — ${clean(src[key])}`)
  }
  if (more) lines.push(`…and ${plural(more, 'more column', 'more columns')} on the row.`)
  // WHY THE LIST MIGHT BE SHORT. Two very different reasons, and a reader
  // auditing an email needs to know which one they are looking at: a genuinely
  // sparse row, or a row this browser holds only a bounded copy of. The
  // snapshot is written when somebody opens the lead's detail page; without
  // one, all that reached here are the fields the receipt itself stored.
  const hasSnapshot = ['score', 'owners', 'estimators', 'contact_phone', 'sales_angles'].some(
    (k) => clean(src[k]),
  )
  if (!hasSnapshot) {
    lines.push(
      'No local snapshot of this lead exists in this browser (nobody on this account has opened its detail page), so the fields above are the bounded copies this receipt itself stored.',
    )
  }
  if (!lines.length) {
    lines.push(`Only the company name (${clean(lead?.name) || 'unnamed'}) was in hand — no row fields reached this receipt.`)
  }
  return lines
}

function rowNode({ id, dependsOn, lane = null, company, facts, lead, recorded = null, exec = [] }) {
  const detail = rowColumnLines(facts, lead)
  const fieldCount = detail.filter((l) => l.includes(' — ')).length
  return withExec(
    withRecord(
      {
        id,
        title: 'The directory row',
        headline: fieldCount
          ? `${plural(fieldCount, 'field', 'fields')} on ${company}'s row were in hand when this was written.`
          : `No row fields for ${company} reached this receipt.`,
        detail,
        lane,
        dependsOn,
        status: 'done',
      },
      recorded,
    ),
    exec,
  )
}

// ── the draft that was REFUSED ──────────────────────────────────────────────
//
// POST /enrich/draft may answer with a DECISION instead of an email, and the
// card then shows the reason where the email would have been
// (Chat.jsx#skippedDraft). This node is what the arrow behind that card opens.
//
// IT EXISTS BECAUSE EVERY SENTENCE THE AUDIT NODE WRITES IS A SENTENCE ABOUT AN
// EMAIL. Composed over a refusal, all of them are false at once: "the email
// quotes no company fact" (there is no email), "Greeting: “Hi there”" (nobody
// was greeted), "Written by the model … from the row alone" (on the code-gate
// path no model was called at all). Those are exactly the invented details this
// whole view exists to catch, so a refusal never reaches that code — it gets a
// node whose every line is about the refusal itself.
//
// THE TWO REFUSAL PATHS ARE DIFFERENT EVENTS, and naming them apart is the
// point. The our-own-domains gate (enrich/handler.py#_draft) decides in CODE,
// before any model call, and its exec record carries no model and no prompt —
// only `gate`, the rule that fired, and `address`, the mailbox that failed it.
// A model refusal (DRAFT_SYSTEM rule 12) carries the model, the prompt id and
// its sha, and was made after reading the thread. "The model decided" and "a
// constant decided" are answers a reader would act on differently, so the line
// is chosen from what the record actually holds and never from a default.
function refusalNode({ id, dependsOn, draft, wrote, recorded = null, exec = [] }) {
  const reason = clean(draft?.skipReason)
  const category = clean(draft?.skipCategory)
  const detail = [
    reason
      ? `The reason it gave, in its own words: “${reason}”`
      : 'It gave no reason at all. Nothing was written for this lead, and nothing was put in its place.',
  ]
  // How many mirrored messages actually reached the route, off the lambda's own
  // exec (inputs.thread). It is the difference between "it read the thread and
  // said no" and "it said no about a lead it was shown no thread for", and it
  // is not derivable from anything else on this receipt.
  const threadCount = Number.isFinite(Number(wrote?.inputs?.thread)) ? Number(wrote.inputs.thread) : null
  const model = clean(wrote?.model)
  const promptId = clean(wrote?.prompt?.id)
  const sha = clean(wrote?.prompt?.sha)
  if (model || promptId || sha) {
    const named = `${model || 'an unnamed model'}${promptId ? `, prompt ${promptId}` : ''}${sha ? ` #${sha}` : ''}`
    detail.push(
      threadCount
        ? `The writer (${named}) was shown ${plural(threadCount, 'message', 'messages')} of this thread and refused to write on it.`
        : `The writer (${named}) refused to write this one. No thread reached it — the refusal was made on the lead record alone.`,
    )
  } else if (wrote) {
    const gate = clean(wrote.gate)
    const address = clean(wrote.address)
    detail.push(
      `Refused in code by ${clean(wrote.function) || 'the enrich lambda'} (${clean(wrote.file) || 'handler.py'}) before any model call${gate ? `: ${gate}` : ''}.`,
    )
    if (address) detail.push(`The address that failed that check: ${address}.`)
  } else {
    detail.push(
      'No execution record came back with this refusal, so the code that decided it cannot be named here — that is an absence, not a guess at what ran.',
    )
  }
  // What a later report counts refusals by. The card never shows it (the reason
  // already says it in words a rep reads); here it is the machine-readable half
  // of the same decision.
  if (category) detail.push(`Counted as: ${category}.`)
  const execs = [
    ...(wrote ? [{ ...wrote, nodeId: id, nodeTitle: 'Why no email was written' }] : []),
    ...(Array.isArray(exec) ? exec : []),
  ]
  return withExec(
    withRecord(
      {
        id,
        title: 'Why no email was written',
        headline: 'No email was written for this lead — the draft API declined to write one.',
        detail,
        lane: null,
        dependsOn,
        // 'done', not 'skipped': the drafting step RAN and reached an answer.
        // 'skipped' in this view means a check that never happened, and a
        // refusal is the opposite of that — it is the step doing its job.
        status: 'done',
      },
      recorded,
    ),
    execs,
  )
}

// ── what the email actually quoted ──────────────────────────────────────────
//
// THE AUDIT NODE. draft.sources[] is the builder's own record of every fact it
// wrote into the email and where that value came from — written by
// outreachDraft.js as it wrote each clause, not reconstructed here. Read
// alongside the email in the panel below it, this is the whole proof: every
// company-specific claim in the text appears here with its origin, and a claim
// that does not appear here is not in the email.
function draftNode({ id, dependsOn, draft, facts, research, meta = null, recorded = null, exec = [] }) {
  // WHO WROTE THE WORDS. The draft's own exec record says: the model on the
  // server (enrich/handler.py#_draft, with the model name), or the browser
  // template in outreachDraft.js — and, when the template stood in for the
  // model, why. Said first, because it is the fact a reader most needs about
  // an email they are about to put their name on.
  const wrote = draft?.exec && typeof draft.exec === 'object' ? draft.exec : null
  // NO EMAIL, NO EMAIL NODE. Read from the draft's own flag first (what the
  // card renders off) and from the server's decision second, so a receipt that
  // carries only one of the two still lands here rather than being described as
  // an email nobody wrote.
  if (draft?.skipped === true || wrote?.decision === 'skip') {
    return refusalNode({ id, dependsOn, draft, wrote, recorded, exec })
  }
  const sources = Array.isArray(draft?.sources) ? draft.sources : []
  const f = facts || {}
  const detail = sources.map((s) => `${s.label}: “${s.value}” — ${s.origin}`)
  if (wrote?.runtime === 'aws-lambda') {
    detail.unshift(
      `Written by the model: ${wrote.model || 'unnamed model'} via ${wrote.function || 'the enrich lambda'} (${wrote.file || 'handler.py'}), prompt ${wrote.prompt?.id || '?'}${wrote.prompt?.sha ? ` #${wrote.prompt.sha}` : ''}${wrote.dossier?.found ? ', from the row plus the full cached dossier' : ', from the row alone (no dossier in the cache)'}.`,
    )
  } else if (wrote?.runtime === 'browser') {
    detail.unshift(
      wrote.fallback
        ? `Assembled by the browser template (${wrote.file || 'src/lib/outreachDraft.js'}) as a FALLBACK — ${wrote.fallback}.`
        : `Assembled by the browser template (${wrote.file || 'src/lib/outreachDraft.js'}) — fixed sentences, no model.`,
    )
  } else {
    detail.unshift(
      'No execution record on this draft — it was saved before drafts recorded what wrote them (the browser template of the time).',
    )
  }
  // The greeting line describes the TEMPLATE's rule; a model draft is under
  // the same rule by prompt, and the line stays true for both.
  detail.push(
    f.first
      ? `Greeting: “Hi ${f.first}” — from contact_name on the row.`
      : 'Greeting: “Hi there” — no contact_name on the row, and a first name is never taken from an email address.',
  )
  // The touch-aware line, on a follow-up only, from the rule that owns it.
  const touch = Number.isFinite(meta?.touch) ? meta.touch : null
  if (touch !== null) {
    const quoted = sources.some((s) => RESEARCH_LABELS.includes(s.label))
    if (!followupPersonalizes(touch)) {
      detail.push(`Touch ${touch} writes no personal line at all, so no company fact was quoted into it.`)
    } else if (!quoted) {
      detail.push('This touch carries a personal line and none of the held facts filled it, so it was left out.')
    }
  }
  // The draft's own record first (it is what wrote the words), the run's
  // staging node after it. The humanize pass the browser ran on the model's
  // text rides on the draft record as `post`.
  const execs = [
    ...(wrote ? [{ ...wrote, nodeId: id, nodeTitle: 'The draft' }] : []),
    ...(Array.isArray(exec) ? exec : []),
  ]
  return withExec(
    withRecord(
      {
        id,
        title: 'What the email used',
        headline: sources.length
          ? `The email quotes ${plural(sources.length, 'fact', 'facts')}, each named below with where its value came from.`
          : 'The email quotes no company fact — it names the company and nothing it cannot stand behind.',
        detail,
        links: researchLinks(researchedEntryOf(draft), research),
        lane: null,
        dependsOn,
        status: 'done',
      },
      recorded,
    ),
    execs,
  )
}

// ── the research dossier ────────────────────────────────────────────────────
//
// Every value the dossier put within reach of this draft, with its field name,
// and the pages it read as real links. The three no-dossier states get one line
// each: what a reader needs is which state this is, not a description of what
// web search is for.
function researchNode({ id, dependsOn, lane = null, company, research, draft, attempted, recorded = null, exec = [], workerExec = null }) {
  if (!research.researched) {
    return withExec(
      withRecord(
        {
          id,
          title: 'The research dossier',
          headline: attempted
            ? `Research ran on ${company}; no dossier reached this draft.`
            : `No dossier on ${company}.`,
          detail: ['The email’s personal clause comes off the directory row instead. No researched claim is made.'],
          lane,
          dependsOn,
          status: 'skipped',
        },
        recorded,
      ),
      exec,
    )
  }
  // The worker's own record — the code that produced THIS dossier, frozen with
  // it when it was researched — outranks the run node's descriptor and comes
  // first. A dossier from before the worker wrote one has none, and the view
  // says so on the run's record instead of inventing a worker record.
  const execs = [
    ...(workerExec && typeof workerExec === 'object'
      ? [{ ...workerExec, nodeId: id, nodeTitle: 'The research dossier' }]
      : []),
    ...(Array.isArray(exec) ? exec : []),
  ]
  const day = formatChicagoDay(research.at)
  const used = researchedEntryOf(draft)
  const detail = []
  for (const [field, value] of [
    ['research_recent_activity', research.recentActivity],
    ['research_buying_signal', research.buyingSignal],
    ['research_current_solution', research.currentSolution],
    ['research_service_area', research.serviceArea],
    ['research_pain_points', research.painPoints],
    ['research_opener', research.opener],
  ]) {
    if (clean(value)) detail.push(`${field} — “${clean(value)}”`)
  }
  detail.push(
    used
      ? `Written into the email: ${used.label.toLowerCase()}.`
      : 'None of it was written into the email — no value fit the one personal clause, so that clause came off the row.',
  )
  // THE EXTERNAL EVIDENCE. Whole URLs ride as structured `links` (the panel
  // renders real anchors); the dossier's bare DOMAINS stay display text,
  // because promoting "apexmasonry.com" to an href would print a homepage the
  // research never opened under the words "pages it read".
  const links = researchLinks(used, research)
  if (research.sources.length) detail.push(`Domains the dossier named: ${research.sources.join(', ')}.`)
  if (!links.length) {
    detail.push(
      research.sources.length
        ? 'It cited those domains but no page URLs — nothing to open here.'
        : 'It cited no source links.',
    )
  }
  if (!workerExec) {
    detail.push(
      'This dossier carries no execution record of its own — it was researched before the enrich-worker recorded what produced it (models, prompt shas). The run step below names the code path.',
    )
  }
  return withExec(
    withRecord(
      {
        id,
        title: 'The research dossier',
        headline: `${research.mode === 'cached' ? 'Cached' : 'Fresh'} OpenAI web-research dossier${day ? `, ${day}` : ''}.`,
        detail,
        links,
        lane,
        dependsOn,
        status: 'done',
      },
      recorded,
    ),
    execs,
  )
}

/**
 * The provenance graph for one INITIAL outreach draft.
 *
 *   ev-row → { ev-history ‖ ev-crm ‖ ev-research } → ev-draft
 *
 * @param {object} lead   the receipt stub — { key, name, email, … }
 * @param {object|string} facts  the merged fact input the draft was built from
 * @param {object} draft  the HUMANIZED draft — { subject, bodyText, sources }
 * @param {object} [prov] the run's own per-lead record — see quote() above.
 *                        Absent on a receipt whose trace was shed, and on every
 *                        receipt from before the trace existed; the nodes then
 *                        show what the stub carries and nothing more.
 * @returns {{ passes: object[], terminalLabel: string }}
 */
export function buildDraftPasses(lead = {}, facts = null, draft = {}, prov = null) {
  const f = readFacts({ ...(typeof facts === 'object' && facts ? facts : {}), name: lead.name, email: lead.email })
  const company = f.company || (typeof facts === 'string' ? facts.trim() : '') || lead.name || 'this company'
  const research = researchOf(lead, facts)

  // ── who was already in a thread ───────────────────────────────────────────
  // The gate a cold intro passes, told with the run's own sentence about which
  // lanes it actually read. An unread lane is an admission, not a pass.
  const contacted = quote(prov, 'contacted')
  const reachable = quote(prov, 'reachable')
  const historyDetail = []
  if (clean(lead.email)) historyDetail.push(`Address the email is addressed to: ${clean(lead.email)}.`)
  // THE REP CHANGED THE PERSON. `recipient` is written on the stub only by the
  // card's dropdown (lib/runHost.js#switchLeadRecipient), after the run: the
  // run's own gate below still describes the address IT picked, and this line
  // is what keeps the two from reading as a contradiction.
  if (lead.recipient && typeof lead.recipient === 'object') {
    const who = [clean(lead.recipient.name), clean(lead.recipient.title) ? `(${clean(lead.recipient.title)})` : '']
      .filter(Boolean)
      .join(' ')
    const addr = clean(lead.recipient.email)
    historyDetail.push(
      `Recipient changed after the run: the rep switched this card to ${who || 'another person at the company'}${
        addr ? ` at ${addr}` : ', whose email address is not on file — the draft greets them by name and names no address'
      }. This draft was written for them, not for the address the run picked.`,
    )
  }
  if (reachable?.note) historyDetail.push(`Address check: ${reachable.note}.`)
  if (!contacted && !reachable) {
    historyDetail.push(
      'This receipt holds no per-step record for this lead, so what the run read here cannot be shown — only that the lead reached the draft.',
    )
  }
  const historyUnread = /was NOT checked|could not be read/i.test(contacted?.note || '')

  // ── the CRM ───────────────────────────────────────────────────────────────
  // What the run read out of HubSpot for THIS company, verbatim. The one
  // disclosure that matters as much as the counts: open tasks are COUNTED and
  // their titles are never read, so a task called "fu demo" is inside a number
  // here and is not, and cannot be, named.
  const hsMatch = quote(prov, 'hs-match')
  const hsDeals = quote(prov, 'hs-deals')
  const hsTasks = quote(prov, 'hs-tasks')
  const hsScreen = quote(prov, 'hs-screen')
  // A 'skipped' verdict means the check did NOT run — the mirror was unreadable,
  // the company had no rollup. The note itself says so and is shown either way,
  // but the disclosures below describe what a read actually looked at, and
  // printing "open tasks were counted, not read" over a check that counted
  // nothing would be a claim about work that never happened. They are gated on
  // the verdict, not on the note existing.
  const ranDeals = Boolean(hsDeals) && hsDeals.verdict !== 'skipped'
  const ranTasks = Boolean(hsTasks) && hsTasks.verdict !== 'skipped'
  const crmDetail = []
  if (hsMatch) crmDetail.push(`Match: ${hsMatch.note}.`)
  if (hsDeals) crmDetail.push(`Open deals: ${hsDeals.note}.`)
  if (hsTasks) crmDetail.push(`Open tasks: ${hsTasks.note}.`)
  if (ranTasks) {
    crmDetail.push(
      'Open tasks were counted, not read: no task title, owner or due date was fetched. A task named “fu demo” is inside that count and cannot be named here.',
    )
  }
  if (ranDeals) {
    crmDetail.push(
      'Deals were read for HubSpot’s own is_closed flag only — no deal name, amount, stage, owner or close date was read.',
    )
  }
  if (ranDeals || ranTasks) crmDetail.push('The CRM was read, never written.')
  const hasCrm = Boolean(hsMatch || hsDeals || hsTasks || hsScreen)
  // The node is an admission, not a pass, when nothing could be read.
  const crmUnread =
    !ranDeals && !ranTasks && (hsMatch?.verdict === 'skipped' || /could not be read|not attempted/i.test(hsMatch?.note || ''))

  // The worker's exec record for this lead's dossier: on the stub (packed by
  // the receipt) or on the merged facts. Research is the run's story, so the
  // stub is read first.
  const workerExec =
    (lead?.research_exec && typeof lead.research_exec === 'object' ? lead.research_exec : null) ||
    (typeof facts === 'object' && facts?.research_exec && typeof facts.research_exec === 'object'
      ? facts.research_exec
      : null)

  const passes = [
    rowNode({
      id: 'ev-row',
      dependsOn: [],
      company,
      facts,
      lead,
      recorded: quote(prov, 'source'),
      exec: runExec(prov, 'source', 'reachable'),
    }),
    withExec(
      withRecord(
        {
          id: 'ev-history',
          title: 'Outreach history',
          headline: contacted?.note
            ? contacted.note.charAt(0).toUpperCase() + contacted.note.slice(1)
            : `Whether anyone at ${company} had already been emailed.`,
          detail: historyDetail,
          lane: 0,
          dependsOn: ['ev-row'],
          status: historyUnread ? 'skipped' : 'done',
        },
        contacted,
      ),
      runExec(prov, 'contacted'),
    ),
    ...(hasCrm
      ? [
          withExec(
            withRecord(
              {
                id: 'ev-crm',
                title: 'HubSpot: deals and tasks',
                headline: hsDeals?.note || hsMatch?.note || 'What the CRM held on this company.',
                detail: crmDetail,
                lane: 1,
                dependsOn: ['ev-row'],
                status: crmUnread ? 'skipped' : 'done',
              },
              hsScreen || hsMatch,
            ),
            runExec(prov, 'hs-match', 'hs-deals', 'hs-tasks', 'hs-screen'),
          ),
        ]
      : []),
    researchNode({
      id: 'ev-research',
      dependsOn: ['ev-row'],
      lane: 2,
      company,
      research,
      draft,
      attempted: Boolean(lead.researchAttempted),
      recorded: quote(prov, 'enriched'),
      exec: runExec(prov, 'enriched'),
      workerExec,
    }),
    draftNode({
      id: 'ev-draft',
      dependsOn: ['ev-history', ...(hasCrm ? ['ev-crm'] : []), 'ev-research'],
      draft,
      facts: f,
      research,
      recorded: quote(prov, 'campaign') || quote(prov, 'audience'),
      exec: runExec(prov, 'audience', 'campaign'),
    }),
  ]

  return { passes, terminalLabel: 'Ready to paste' }
}

// ── the follow-up gate ──────────────────────────────────────────────────────
//
// The same four steps for an email touch and a call touch, because the same
// reads admitted the lead: what thread this follows, how long ago, whether a
// reply came, and whether anything excluded it.
//
// EVERY NUMBER is a value the caller actually computed and passed in `meta`. A
// missing meta field means the sentence that would have carried it is NOT
// written; nothing here re-derives, estimates or decorates.
function threadNode({ lead, meta, prov, id = 'fu-thread' }) {
  const detail = []
  const subject = clean(lead?.lastSubject || meta?.lastSubject)
  const snippet = clean(lead?.lastSnippet || meta?.lastSnippet)
  const bounced = Number.isFinite(lead?.bouncedCount)
    ? lead.bouncedCount
    : Number.isFinite(meta?.bouncedCount)
      ? meta.bouncedCount
      : 0
  const touchCount = Number.isFinite(meta?.touchCount) ? meta.touchCount : null
  if (subject) detail.push(`Subject of our last email: “${subject}”`)
  if (snippet) detail.push(`Its opening line: “${snippet}”`)
  if (subject || snippet) {
    detail.push(
      'Both are read from the HubSpot mirror’s own hs_email_subject / hs_email_text — not reconstructed, and never the whole body.',
    )
  }
  if (bounced > 0) {
    detail.push(`${plural(bounced, 'earlier send', 'earlier sends')} bounced or were never delivered — not counted as touches.`)
  }
  // WHAT WAS NOT CONSULTED, said once and plainly: this operation reads the
  // mirror's email engagements and nothing else, so a reader never has to
  // wonder whether a deal or a task influenced the follow-up.
  detail.push(
    'This run read the HubSpot mirror’s email engagements only — no deal, task, note, meeting or logged call was read on this chain.',
  )
  return withExec(
    withRecord(
    {
      id,
      title: 'The thread this follows',
      headline: subject
        ? `Follows the email “${subject}”.`
        : touchCount !== null
          ? `Follows ${plural(touchCount, 'outbound touch', 'outbound touches')} to this lead.`
          : 'Follows earlier outbound to this lead.',
      detail,
      lane: null,
      dependsOn: [],
      status: 'done',
    },
    quote(prov, 'match'),
    ),
    runExec(prov, 'history', 'match'),
  )
}

function clockNode({ meta, prov, dependsOn, id = 'fu-clock' }) {
  const day = formatChicagoDay(meta?.lastTouchAt)
  const daysSince = Number.isFinite(meta?.daysSince) ? meta.daysSince : null
  const detail = []
  if (daysSince !== null) {
    detail.push('Business days, read on the America/Chicago calendar Bo works. Weekends are not counted.')
  }
  return withExec(
    withRecord(
    {
      id,
      title: 'Time since the last touch',
      headline:
        day && daysSince !== null
          ? `Last touched ${day} — ${plural(daysSince, 'business day', 'business days')} ago.`
          : day
            ? `Last touched ${day}.`
            : daysSince !== null
              ? `Last touched ${plural(daysSince, 'business day', 'business days')} ago.`
              : 'Past the wait gate.',
      detail,
      lane: null,
      dependsOn,
      status: 'done',
    },
    quote(prov, 'wait-gate'),
    ),
    runExec(prov, 'wait-gate'),
  )
}

function freezeNode({ meta, prov, dependsOn }) {
  const unchecked = Boolean(meta?.replyUnchecked)
  const recorded = quote(prov, 'freeze')
  const detail = unchecked
    ? ['Nothing was cleared here. Check the thread by hand before this goes anywhere.']
    : []
  // NO echo of the record here: when the run wrote one, the panel is already
  // showing it verbatim above, and the same sentence twice reads as two
  // findings.
  detail.push(
    'A reply’s subject and text are never carried onto a drafted lead — a lead that really replied is pulled out of the cadence before a draft exists.',
  )
  return withExec(
    withRecord(
      {
        id: 'fu-freeze',
        title: 'Reply check',
        headline: unchecked
          ? 'The reply sources could not be read — a reply cannot be ruled out.'
          : recorded?.note || 'No inbound after our last outbound in the sources read.',
        detail,
        lane: 0,
        dependsOn,
        status: unchecked ? 'skipped' : 'done',
      },
      recorded,
    ),
    runExec(prov, 'freeze'),
  )
}

function exclNode({ meta, prov, dependsOn }) {
  const unchecked = Boolean(meta?.exclUnchecked)
  return withExec(
    withRecord(
    {
      id: 'fu-excl',
      title: 'Said-no check',
      headline: unchecked
        ? 'The outcome log could not be read — the said-no check was skipped.'
        : 'No logged “no” against this lead, and the cadence is not complete.',
      detail: [
        unchecked
          ? 'Nothing was excluded on a fact this run could not read, and nothing was cleared either — review before sending.'
          : 'Neither applied here, which is the only reason this item exists.',
        'This workspace holds no unsubscribe or do-not-contact data at all — no such column on the rows, no opt-out event type. Nothing to check, which is not the same as “0 opted out”.',
      ],
      lane: 1,
      dependsOn,
      status: unchecked ? 'skipped' : 'done',
    },
    quote(prov, 'excl'),
    ),
    runExec(prov, 'excl'),
  )
}

// WHERE THIS TOUCH SITS — the node that replaced fu-cadence. What left it: the
// arithmetic ("touch number = deduped outbound touches + 1") and the recital of
// all seven touches. What is here instead is the count itself, the date behind
// it, and an honest statement of what the receipt does NOT hold: the individual
// dates of the earlier touches.
function positionNode({ meta, prov, dependsOn }) {
  const touch = Number.isFinite(meta?.touch) ? meta.touch : null
  const touchCount = Number.isFinite(meta?.touchCount) ? meta.touchCount : null
  const day = formatChicagoDay(meta?.lastTouchAt)
  const detail = []
  if (touchCount !== null) {
    detail.push(
      `${plural(touchCount, 'prior outbound touch', 'prior outbound touches')} on record — counted as distinct America/Chicago calendar days on which a delivered email went out.`,
    )
    if (day) detail.push(`Last of them: ${day}.`)
    detail.push('The receipt holds that count and that date. The dates of the earlier touches individually are not stored.')
  }
  return withExec(
    withRecord(
      {
        id: 'fu-position',
        title: 'Where this touch sits',
        headline: touch !== null ? `Touch ${touch} — ${touchLabel(touch)}.` : 'Placed in the cadence.',
        detail,
        lane: null,
        dependsOn,
        status: 'done',
      },
      quote(prov, 'rank') || quote(prov, 'cadence'),
    ),
    runExec(prov, 'cadence', 'rank'),
  )
}

/**
 * The provenance graph for one FOLLOW-UP email draft.
 *
 *   fu-thread → fu-clock → { fu-freeze ‖ fu-excl } → fu-position → fu-row → fu-draft
 *
 * NO RESEARCH NODE. The follow-up operation buys no research, so a research
 * node on this chain could only ever announce that nothing happened. When a
 * follow-up genuinely DID quote a researched fact, its citation appears where
 * it belongs — on fu-draft, as the sources[] line that fact was written from,
 * with the dossier's real pages as links.
 *
 * @param {object} lead   the receipt stub
 * @param {object|string} facts  the merged fact input
 * @param {object} draft  the HUMANIZED draft
 * @param {object} meta   { touch, daysSince, lastTouchAt, touchCount,
 *                          replyUnchecked?, exclUnchecked? } — the REAL values
 *                        the run computed, plus its admissions.
 * @param {object} [prov] the run's own per-lead record
 */
export function buildFollowupDraftPasses(lead = {}, facts = null, draft = {}, meta = {}, prov = null) {
  const f = readFacts({ ...(typeof facts === 'object' && facts ? facts : {}), name: lead.name, email: lead.email })
  const company = f.company || lead.name || 'this company'
  const research = researchOf(lead, facts)
  // The worker's exec record for the dossier the quoted fact came from — the
  // same record buildDraftPasses reads. Shown on fu-draft only when the draft
  // ACTUALLY quoted a researched fact; there is no research node here.
  const workerExec =
    (lead?.research_exec && typeof lead.research_exec === 'object' ? lead.research_exec : null) ||
    (typeof facts === 'object' && facts?.research_exec && typeof facts.research_exec === 'object'
      ? facts.research_exec
      : null)
  const researchExec =
    workerExec && researchedEntryOf(draft) ? [{ ...workerExec, nodeId: 'fu-draft', nodeTitle: 'The research dossier' }] : []

  const passes = [
    threadNode({ lead, meta, prov }),
    clockNode({ meta, prov, dependsOn: ['fu-thread'] }),
    freezeNode({ meta, prov, dependsOn: ['fu-clock'] }),
    exclNode({ meta, prov, dependsOn: ['fu-clock'] }),
    positionNode({ meta, prov, dependsOn: ['fu-freeze', 'fu-excl'] }),
    rowNode({
      id: 'fu-row',
      dependsOn: ['fu-position'],
      company,
      facts,
      lead,
      exec: runExec(prov, 'match'),
    }),
    draftNode({
      id: 'fu-draft',
      dependsOn: ['fu-row'],
      draft,
      facts: f,
      research,
      meta,
      recorded: quote(prov, 'campaign') || quote(prov, 'audience'),
      exec: [...researchExec, ...runExec(prov, 'audience', 'campaign')],
    }),
  ]

  return { passes, terminalLabel: 'Ready to paste' }
}

/**
 * The provenance graph for one CALL-PREP item (touches 4 and 7).
 *
 *   call-thread → call-clock → call-checks → call-row → call-points
 *
 * @param {object} lead   the call stub — { key, name, email, lastSubject?, … }
 * @param {object|string} facts  local intel snapshot, if this account holds one
 * @param {object} meta   { touch, daysSince, lastTouchAt, touchCount, points,
 *                          phone, replyUnchecked?, exclUnchecked?, … }
 * @param {object} [prov] the run's own per-lead record. A call row is not one of
 *                        the leads a receipt keeps a per-step record for, so
 *                        this is normally absent — and the chain says so.
 */
export function buildCallPasses(lead = {}, facts = null, meta = {}, prov = null) {
  const f = readFacts({ ...(typeof facts === 'object' && facts ? facts : {}), name: lead.name, email: lead.email })
  const company = f.company || lead.name || 'this company'
  const points = Array.isArray(meta.points) ? meta.points : []
  const phone = clean(meta.phone)
  const replyUnchecked = Boolean(meta.replyUnchecked)
  const exclUnchecked = Boolean(meta.exclUnchecked)

  const thread = threadNode({ lead, meta, prov, id: 'call-thread' })
  // A call row never carries a per-step record: the receipt keeps one only for
  // the leads it drafted an email for. Said on the node rather than left as a
  // silent gap.
  if (!thread.recorded) {
    thread.detail = [
      ...thread.detail,
      'This receipt keeps a per-step record only for the leads it drafted an email for, and a call touch is not one of them.',
    ]
  }

  const checksDetail = []
  if (replyUnchecked) {
    checksDetail.push('The reply sources could not be read when this call was queued — a reply cannot be ruled out. Check the thread before dialing.')
  }
  if (exclUnchecked) {
    checksDetail.push('The outcome log could not be read either, so the said-no check was skipped.')
  }
  if (!replyUnchecked && !exclUnchecked) {
    checksDetail.push('Both were readable when this call was queued, and neither excluded this lead. Nothing more than that is claimed.')
  }

  const pointDetail = [
    ...points,
    phone
      ? `Best number: ${phone} — from contact_phone, or the first phone= in the packed contacts blob. Not looked up anywhere.`
      : 'No phone number is held for this lead. Finding the number is Bo’s step, not the app’s.',
  ]

  const passes = [
    thread,
    clockNode({ meta, prov, dependsOn: ['call-thread'], id: 'call-clock' }),
    withExec({
      id: 'call-checks',
      title: 'Reply and said-no checks',
      headline:
        replyUnchecked || exclUnchecked
          ? 'One of the checks behind this call could not be run.'
          : 'Neither check excluded this lead.',
      detail: checksDetail,
      lane: null,
      dependsOn: ['call-clock'],
      status: replyUnchecked || exclUnchecked ? 'skipped' : 'done',
    }, runExec(prov, 'freeze', 'excl')),
    rowNode({
      id: 'call-row',
      dependsOn: ['call-checks'],
      company,
      facts,
      lead,
      exec: runExec(prov, 'match'),
    }),
    withExec({
      id: 'call-points',
      title: 'The talking points',
      headline: points.length
        ? `${plural(points.length, 'talking point', 'talking points')} for a sub-two-minute call.`
        : 'Call prep staged — the points travel with the worklist item.',
      detail: pointDetail,
      metric: points.length
        ? { value: points.length, label: points.length === 1 ? 'talking point' : 'talking points' }
        : undefined,
      lane: null,
      dependsOn: ['call-row'],
      status: 'done',
    }, runExec(prov, 'rank', 'worklist')),
  ]

  return { passes, terminalLabel: 'Bo dials — the app never calls' }
}

export default buildDraftPasses
